import { ThrottleConfig, ThrottleAction, ThrottleStatus } from '../types/user';

const THROTTLE_CONFIG_STORAGE_KEY = 'scan-store-retrieve-throttle-config';
const THROTTLE_COUNTERS_STORAGE_KEY = 'scan-store-retrieve-throttle-counters';

// Default throttle configurations
const DEFAULT_THROTTLE_CONFIGS: ThrottleConfig[] = [
  {
    id: 'throttle-add',
    action: 'add',
    maxRequests: 100,
    timeWindowSeconds: 3600, // 1 hour
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-edit',
    action: 'edit',
    maxRequests: 200,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-update',
    action: 'update',
    maxRequests: 200,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-delete',
    action: 'delete',
    maxRequests: 50,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-view',
    action: 'view',
    maxRequests: 500,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-download',
    action: 'download',
    maxRequests: 100,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
  {
    id: 'throttle-share',
    action: 'share',
    maxRequests: 50,
    timeWindowSeconds: 3600,
    enabled: false,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  },
];

interface ThrottleCounter {
  userId: string;
  action: ThrottleAction;
  count: number;
  windowStart: number; // timestamp
}

const initializeThrottleConfigs = (): ThrottleConfig[] => {
  const stored = localStorage.getItem(THROTTLE_CONFIG_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  localStorage.setItem(THROTTLE_CONFIG_STORAGE_KEY, JSON.stringify(DEFAULT_THROTTLE_CONFIGS));
  return DEFAULT_THROTTLE_CONFIGS;
};

const getThrottleCounters = (): ThrottleCounter[] => {
  const stored = localStorage.getItem(THROTTLE_COUNTERS_STORAGE_KEY);
  if (stored) {
    return JSON.parse(stored);
  }
  return [];
};

const saveThrottleCounters = (counters: ThrottleCounter[]): void => {
  localStorage.setItem(THROTTLE_COUNTERS_STORAGE_KEY, JSON.stringify(counters));
};

export const throttleService = {
  /**
   * Get all throttle configurations
   */
  getThrottleConfigs(): ThrottleConfig[] {
    return initializeThrottleConfigs();
  },

  /**
   * Get throttle configuration for a specific action
   */
  getThrottleConfig(action: ThrottleAction): ThrottleConfig | null {
    const configs = initializeThrottleConfigs();
    return configs.find(c => c.action === action) || null;
  },

  /**
   * Update throttle configuration (admin only)
   */
  updateThrottleConfig(configId: string, updates: Partial<ThrottleConfig>): ThrottleConfig {
    const configs = initializeThrottleConfigs();
    const configIndex = configs.findIndex(c => c.id === configId);
    
    if (configIndex === -1) {
      throw new Error('Throttle configuration not found');
    }

    configs[configIndex] = {
      ...configs[configIndex],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(THROTTLE_CONFIG_STORAGE_KEY, JSON.stringify(configs));
    return configs[configIndex];
  },

  /**
   * Check if action is throttled
   */
  checkThrottle(userId: string, action: ThrottleAction): ThrottleStatus {
    const config = this.getThrottleConfig(action);
    
    if (!config || !config.enabled) {
      return {
        action,
        currentCount: 0,
        maxRequests: config?.maxRequests || 0,
        timeWindowSeconds: config?.timeWindowSeconds || 0,
        resetAt: new Date().toISOString(),
        isThrottled: false,
      };
    }

    const counters = getThrottleCounters();
    const now = Date.now();
    const windowStart = now - (config.timeWindowSeconds * 1000);

    // Find or create counter for this user/action
    let counter = counters.find(
      c => c.userId === userId && c.action === action
    );

    if (!counter || counter.windowStart < windowStart) {
      // Reset counter if window expired
      counter = {
        userId,
        action,
        count: 0,
        windowStart: now,
      };
    }

    const resetAt = new Date(counter.windowStart + (config.timeWindowSeconds * 1000)).toISOString();
    const isThrottled = counter.count >= config.maxRequests;

    return {
      action,
      currentCount: counter.count,
      maxRequests: config.maxRequests,
      timeWindowSeconds: config.timeWindowSeconds,
      resetAt,
      isThrottled,
    };
  },

  /**
   * Increment throttle counter
   */
  incrementThrottle(userId: string, action: ThrottleAction): void {
    const config = this.getThrottleConfig(action);
    if (!config || !config.enabled) return;

    const counters = getThrottleCounters();
    const now = Date.now();
    const windowStart = now - (config.timeWindowSeconds * 1000);

    let counter = counters.find(
      c => c.userId === userId && c.action === action
    );

    if (!counter || counter.windowStart < windowStart) {
      counter = {
        userId,
        action,
        count: 0,
        windowStart: now,
      };
      counters.push(counter);
    }

    counter.count++;
    saveThrottleCounters(counters);
  },

  /**
   * Reset throttle counter for a user/action
   */
  resetThrottle(userId: string, action: ThrottleAction): void {
    const counters = getThrottleCounters();
    const filtered = counters.filter(
      c => !(c.userId === userId && c.action === action)
    );
    saveThrottleCounters(filtered);
  },

  /**
   * Reset all throttle counters (admin only)
   */
  resetAllThrottles(): void {
    saveThrottleCounters([]);
  },
};

